import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Building2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/States";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";

function MessageCard({ m, onRead }) {
  const markRead = async () => {
    try { await api.post(`/messages/${m.id}/read`); onRead(m.id); window.dispatchEvent(new Event("messages-updated")); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid={`message-${m.id}`} className={`rounded-2xl border bg-surface p-6 ${m.read ? "border-line" : "border-aqua shadow-[0_10px_30px_-20px_rgba(14,159,154,.6)]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-semibold text-ink">
            {!m.read && <span data-testid={`message-unread-${m.id}`} className="h-2 w-2 rounded-full bg-aqua-solid" />}{m.sender_name}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-2">
            <a href={`mailto:${m.sender_email}`} data-testid={`message-email-${m.id}`} className="inline-flex items-center gap-1 text-aqua hover:underline"><Mail className="h-3.5 w-3.5" />{m.sender_email}</a>
            {m.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-ink-3" />{m.company}</span>}
          </p>
        </div>
        <span className="text-xs text-ink-3">{new Date(m.created_at).toLocaleString()}</span>
      </div>
      <p className="mt-4 whitespace-pre-wrap rounded-lg bg-canvas-2 p-4 text-sm leading-relaxed text-ink-2">{m.message}</p>
      {m.kind === "invite" && <Button asChild size="sm" className="mr-2 mt-3 rounded-full bg-aqua-solid hover:bg-aqua-solid-hover"><Link data-testid={`message-view-job-${m.id}`} to="/jobs">View job{m.job_title ? `: ${m.job_title}` : ""}</Link></Button>}
      {!m.read && <Button size="sm" variant="ghost" data-testid={`message-mark-read-${m.id}`} onClick={markRead} className="mt-3 rounded-full">Mark as read</Button>}
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/messages").then((r) => setD(r.data)).catch((e) => setErr(formatError(e))); }, []);
  if (err) return <ErrorState message={err} />;
  if (!d) return <Loading />;
  const onRead = (id) => setD({ unread: d.unread - 1, items: d.items.map((m) => (m.id === id ? { ...m, read: true } : m)) });
  const off = !(user.showcase_public && user.allow_contact);
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Messages" title="Employer messages" text="Messages employers sent from your certificate showcase. Your email address is never shown to them; reply only if you want to." />
      {off && (
        <div data-testid="messages-contact-off" className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <ShieldAlert className="h-5 w-5 shrink-0" /><span>Employers can't message you from your showcase right now (you'll still receive job invites). <Link to="/profile" className="font-semibold underline">Turn on employer contact in Profile</Link> to get messages too.</span>
        </div>
      )}
      {d.items.length > 0 && <p data-testid="messages-unread-count" className="mb-4 text-sm text-ink-3"><b className="text-ink">{d.unread}</b> unread</p>}
      {d.items.length === 0 ? <EmptyState testId="messages-empty" title="No messages yet" text="When employers contact you from your showcase, their messages will appear here." /> : (
        <div data-testid="messages-list" className="flex flex-col gap-4">{d.items.map((m) => <MessageCard key={m.id} m={m} onRead={onRead} />)}</div>
      )}
      <p className="mt-6 text-xs text-ink-3">IDDCR doesn't verify senders. Take care before sharing personal details.</p>
    </div>
  );
}
